import { Canvas, useFrame } from '@react-three/fiber';
import { Float, MeshDistortMaterial, OrbitControls, Stars } from '@react-three/drei';
import { Suspense, useRef } from 'react';
import * as THREE from 'three';

/**
 * FloatingMark3D - the hero 3D centerpiece for landing/empty states.
 * A morphing distorted sphere wrapped in a hex shield. Cheap on perf
 * (one mesh, no shadows, on-demand frameloop is fine for hero).
 */
export function FloatingMark3D({ height = 380 }: { height?: number }) {
  return (
    <div style={{ height }} className="relative w-full">
      <Canvas camera={{ position: [0, 0, 4.5], fov: 45 }} dpr={[1, 2]}>
        <Suspense fallback={null}>
          <ambientLight intensity={0.5} />
          <directionalLight position={[3, 4, 5]} intensity={1.5} color="#f15e27" />
          <directionalLight position={[-3, 2, -2]} intensity={1.2} color="#5485c5" />
          <pointLight position={[0, 0, 6]} intensity={0.8} color="#ffffff" />
          <Float speed={1.2} rotationIntensity={0.6} floatIntensity={0.8}>
            <DistortedShield />
          </Float>
          <Stars radius={50} depth={50} count={1500} factor={4} fade speed={1} />
          <OrbitControls
            enableZoom={false}
            enablePan={false}
            autoRotate
            autoRotateSpeed={0.6}
            minPolarAngle={Math.PI / 2.5}
            maxPolarAngle={Math.PI / 1.6}
          />
        </Suspense>
      </Canvas>
      {/* Gradient mask to soften edges */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, transparent 50%, hsl(var(--background)) 100%)',
      }} />
    </div>
  );
}

function DistortedShield() {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame((_state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * 0.15;
    }
  });
  return (
    <group>
      {/* Outer hex shield - using IcosahedronGeometry for flat shading effect */}
      <mesh ref={meshRef}>
        <icosahedronGeometry args={[1.4, 1]} />
        <meshStandardMaterial
          color="#f15e27"
          metalness={0.4}
          roughness={0.3}
          emissive="#f15e27"
          emissiveIntensity={0.15}
          flatShading
          wireframe={false}
        />
      </mesh>
      {/* Wireframe overlay for that "secure / encrypted" vibe */}
      <mesh>
        <icosahedronGeometry args={[1.42, 1]} />
        <meshBasicMaterial color="#5485c5" wireframe transparent opacity={0.35} />
      </mesh>
      {/* Inner energy core */}
      <mesh>
        <sphereGeometry args={[0.6, 32, 32]} />
        <MeshDistortMaterial
          color="#5485c5"
          attach="material"
          distort={0.4}
          speed={2.5}
          roughness={0.1}
          metalness={0.7}
          emissive="#182a52"
          emissiveIntensity={0.4}
        />
      </mesh>
    </group>
  );
}

/**
 * Generic R3F canvas wrapper - used by all 3D widgets that need scoped
 * lighting + camera defaults.
 */
export function R3FCanvas({
  children,
  height = 320,
  cameraPos = [0, 0, 5],
}: {
  children: React.ReactNode;
  height?: number;
  cameraPos?: [number, number, number];
}) {
  return (
    <div style={{ height }} className="w-full relative rounded-lg overflow-hidden">
      <Canvas camera={{ position: cameraPos, fov: 45 }} dpr={[1, 2]}>
        <Suspense fallback={null}>
          <ambientLight intensity={0.6} />
          <directionalLight position={[3, 5, 3]} intensity={1.2} color="#ffffff" />
          {children}
        </Suspense>
      </Canvas>
    </div>
  );
}
